import {IPendingLoginExternalLinkHandler} from "./pending-login.external-link-handler.interface";
import {
    ExternalLinkInIframeHandlerBase,
    ExternalLinkInIframeRendererParams
} from "../external-link-in-iframe-handler-base";
import {GNWP_SOURCES} from "../../../gnwp/gnwp-consts";
import {ExternalLinksZIndexes} from "../../external-links-z-indexes";
import {CIPropRead} from "../../../../../utils/case-insensitive-property-reader";
import {IOpenPendingLoginOptions} from "../../external-links.service.interface";
import {NullableUndefinedNumber, NullableUndefinedString} from "../../../../../common/types/nullable-types";
import {CyClass} from "../../../../../common/cy-classes";

interface ITokenAndOriginInformation {
    token: NullableUndefinedString;
    sourceProductPackage: NullableUndefinedNumber
}

export class PendingLoginExternalLinkHandler extends ExternalLinkInIframeHandlerBase implements IPendingLoginExternalLinkHandler {

    async openPendingLogin(options: IOpenPendingLoginOptions): Promise<void> {
        const url = await this._getGnwpUrl(options);
        await this._showExternalLinkDialog(url);
    }

    protected async _getGnwpUrl(options: IOpenPendingLoginOptions): Promise<string> {
        return await (await this.services.gnwp).getPendingLoginUrl({
            token: options.token
        });
    }

    protected _getGnwpSource(): GNWP_SOURCES {
        return GNWP_SOURCES.PENDING_LOGIN;
    }

    protected _getIframeRendererParams(): ExternalLinkInIframeRendererParams {
        return {
            maxWidth: 1014,
            maxHeight: 702,
            zIndex: ExternalLinksZIndexes.PendingAction,
            httpVerb: 'POST',
            analyticsAction: this._getGnwpSource(),
            hideCloseButton: false,
            iframeExtraCssClass: CyClass('cy-pending-login-external-link-container')
        };
    }

    protected async _onPostMessageReceived(message: MessageEvent): Promise<void> {
        const actionId = this._extractActionId(message);
        if(!actionId) {
            return;
        }

        switch (actionId.toLowerCase()) {
            case 'pending':
                await this._closePendingLogin(this._extractTokenAndOriginInformation(message));
                break;
            case '73':
                await this._openLoginForm();
                break;
            case '75':
                await this._openUploadDocuments(this._extractTokenAndOriginInformation(message));

        }
    }

    protected _extractTokenAndOriginInformation(message: MessageEvent): ITokenAndOriginInformation {
        const messageData = this._parseMessageData(message);
        const token = CIPropRead.getStringProperty('Token', messageData);
        if(!token) {
            return {
                token: null,
                sourceProductPackage: null
            };
        }

        const originInformation = CIPropRead.getProperty('originInformation', messageData);
        const sourceProductPackage = CIPropRead.getNumericProperty('originProductPackageID', originInformation);

        return {
            token: token,
            sourceProductPackage: sourceProductPackage
        };

    }

    protected async _openLoginForm(): Promise<void> {
        this._closeExternalLinkDialog();
        await this.services.user.openLogin();
    }

    protected async  _closePendingLogin(options: ITokenAndOriginInformation): Promise<void> {
        this._closeExternalLinkDialog();
        if(options.token) {
            await this.services.user.runAutoLogin(options);
        }
    }

    protected async _openUploadDocuments(options: ITokenAndOriginInformation): Promise<void> {
        this._closeExternalLinkDialog();

        if(options.token) {
            await this.services.user.runAutoLogin({
                token: options.token,
                sourceProductPackage: options.sourceProductPackage
            });
            await (await this.services.externalLinks).openUploadDocumentFromMyAccount();
        } else {
            this.services.logger.error(`Received open upload documents event from pending login but they didn't provide a token`)
        }
    }
}